import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { MTGCard } from 'types/interfaces/mtgCard';
import {
  cardImageInputsFromCard,
  getCardImageUrl,
  makeCardImageErrorHandler,
} from 'utils/cardImageFallback';
import CardInfoCard from './CardInfoCard';
import CardActionMenu from './CardActionMenu';
import Points from 'components/Points';
import { decimalToPoints } from 'utils/decimalUtils';
import './ResultCard.css';

/** Below this rendered width the face drops its type line and signal rows.
 *  Measured, not derived from the size letter: at 1440px the grid renders
 *  S at 138.8px and M at 169.8px, and M grows past 190px when the filter
 *  panel is hidden — a letter-based rule would flip M's content on a
 *  filter toggle. */
const COMPACT_WIDTH = 150;

interface ResultCardProps {
  card: MTGCard;
  isHovered: boolean;
  onHover: () => void;
  onLeave: () => void;
  isMobile: boolean;
  /** True when this card owns the single open action menu. */
  isMenuOpen: boolean;
  onOpenMenu: () => void;
  onCloseMenu: () => void;
}

/** Backend sends scryfallId; the type declares scryfallID. Accept either. */
const getScryfallId = (card: MTGCard): string =>
  (card as unknown as { scryfallId?: string }).scryfallId ?? card.scryfallID;

/**
 * Points for a card, or null when the backend has no price for it.
 *
 * Never estimated and never coerced to 0 — an unpriced card renders "???"
 * rather than claiming to be worthless.
 */
const getPoints = (card: MTGCard, foil: boolean): number | null => {
  const anyCard = card as unknown as {
    nonFoilPoints?: number | null;
    foilPoints?: number | null;
    pointValue?: number | null;
    price?: Array<{ usd?: unknown; usdFoil?: unknown }> | null;
  };

  if (foil) {
    if (anyCard.foilPoints != null) return anyCard.foilPoints;
    const usdFoil = anyCard.price?.[0]?.usdFoil;
    return usdFoil != null ? decimalToPoints(usdFoil) : null;
  }

  if (anyCard.nonFoilPoints != null) return anyCard.nonFoilPoints;
  if (anyCard.pointValue != null) return anyCard.pointValue;
  const usd = anyCard.price?.[0]?.usd;
  return usd != null ? decimalToPoints(usd) : null;
};

export const ResultCard: React.FC<ResultCardProps> = ({
  card,
  isHovered,
  onHover,
  onLeave,
  isMobile,
  isMenuOpen,
  onOpenMenu,
  onCloseMenu,
}) => {
  // Step state lives in a ref, never a dataset attribute: React re-renders reset
  // dataset values and the handler would loop forever.
  const fallbackStepRef = useRef<Map<string | number, number>>(new Map());
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [isCompact, setIsCompact] = useState(false);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);

  // Measure the rendered card, not the size letter — see COMPACT_WIDTH.
  useLayoutEffect(() => {
    const node = cardRef.current;
    if (!node) return undefined;

    const apply = (width: number) => setIsCompact(width > 0 && width <= COMPACT_WIDTH);
    apply(node.getBoundingClientRect().width);

    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width;
      if (width !== undefined) apply(width);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // Keep the popover anchored while the page scrolls under it. The menu also
  // closes on scroll, so this only has to survive the frame before that fires.
  useEffect(() => {
    if (!isMenuOpen || isMobile) return;
    setAnchorRect(cardRef.current?.getBoundingClientRect() ?? null);
  }, [isMenuOpen, isMobile]);

  const scryfallId = getScryfallId(card);
  const imageInputs = cardImageInputsFromCard(card);
  const nonFoilPrice = getPoints(card, false);
  const foilPrice = getPoints(card, true);
  // holderCount IS the for-trade count — it shares one predicate with the
  // Available to receive filter, so the badge and the toggle cannot disagree.
  const offeredCount = card.holderCount;

  const signal = {
    offeredCount,
    wantedCount: card.wantedCount,
  };

  // Clicking opens the action menu; it deliberately no longer navigates.
  // Navigation moved to the face's title link and the menu's "Open card
  // details" row, which carries the Enter shortcut.
  const handleClick = () => {
    if (isMenuOpen) onCloseMenu();
    else onOpenMenu();
  };

  return (
    <div className="result-card-cell" data-testid="result-card">
      <div
        ref={cardRef}
        className="result-card"
        role="button"
        tabIndex={0}
        aria-haspopup="menu"
        aria-expanded={isMenuOpen}
        onMouseEnter={!isMobile ? onHover : undefined}
        onMouseLeave={!isMobile ? onLeave : undefined}
        onClick={handleClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick();
          }
        }}
      >
        {isHovered && !isMobile ? (
          <CardInfoCard
            title={card.name}
            altName={card.printedName || card.flavorName || undefined}
            cardId={scryfallId}
            setName={card.setName}
            setCode={card.setCode}
            setIconUrl={card.setIconUrl}
            collectorNumber={card.collectorNumber || undefined}
            typeLine={card.typeLine || undefined}
            nonFoilPrice={nonFoilPrice ?? undefined}
            foilPrice={foilPrice ?? undefined}
            signal={signal}
            compact={isCompact}
            onOpenActions={onOpenMenu}
          />
        ) : (
          <img
            className="result-card-image"
            src={getCardImageUrl(imageInputs, 'normal')}
            alt={card.name}
            loading="lazy"
            onError={makeCardImageErrorHandler(imageInputs, fallbackStepRef, scryfallId, 'normal')}
          />
        )}
      </div>

      {/* Outside the art box, which clips its own overflow. Points renders
          through the brand coin component; an unpriced card shows "???" rather
          than claiming to be worth nothing. */}
      <div className="result-card-meta">
        {nonFoilPrice !== null ? (
          <Points type="small" points={nonFoilPrice} />
        ) : (
          <span className="result-card-unpriced">???</span>
        )}
        {/* "offering" counts people, not copies — holderCount is a
            COUNT(DISTINCT userId). */}
        {offeredCount !== undefined && (
          <span className="result-card-holders">{offeredCount} offering</span>
        )}
      </div>

      {isMenuOpen && (
        <CardActionMenu
          card={{
            name: card.name,
            id: card.id,
            scryfallId,
            setCode: card.setCode,
            collectorNumber: card.collectorNumber || undefined,
            points: nonFoilPrice,
          }}
          anchorRect={anchorRect}
          isMobile={isMobile}
          onClose={onCloseMenu}
          returnFocusTo={cardRef.current}
          offeredCount={offeredCount}
        />
      )}
    </div>
  );
};

export default ResultCard;
